import { useSelector } from 'react-redux';
import { useMemo } from 'react';

export const useThemeColors = () => {
  const { menuColor, customMenuColor, greyMenuColor, customGreyMenuColor } = useSelector(state => state.users);

  // Helper function to generate colors from hex
  const generateColorsFromHex = (hexColor) => {
    // Convert hex to RGB
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Generate darker and lighter variations
    const darken = (amount) => {
      return `rgb(${Math.max(0, r - amount)}, ${Math.max(0, g - amount)}, ${Math.max(0, b - amount)})`;
    };
    
    return {
      primary: hexColor,
      primaryHover: darken(20),
      primaryActive: darken(40)
    };
  };

  // Get button colors based on user preference
  const getButtonColors = (color = 'blue', customHex = null) => {
    // Handle custom colors
    if (color === 'custom' && customHex) {
      const colors = generateColorsFromHex(customHex);
      return {
        primary: colors.primary,
        primaryHover: colors.primaryHover,
        primaryActive: colors.primaryActive,
        // Tailwind classes for inline styles fallback
        primaryClass: '',
        primaryHoverClass: '',
        primaryActiveClass: ''
      };
    }
    
    const colorMap = {
      blue: {
        primary: '#2563eb', // blue-600
        primaryHover: '#1d4ed8', // blue-700
        primaryActive: '#1e40af', // blue-800
        primaryClass: 'bg-blue-600',
        primaryHoverClass: 'hover:bg-blue-700',
        primaryActiveClass: 'active:bg-blue-800'
      },
      indigo: {
        primary: '#4f46e5', // indigo-600
        primaryHover: '#4338ca', // indigo-700
        primaryActive: '#3730a3', // indigo-800
        primaryClass: 'bg-indigo-600',
        primaryHoverClass: 'hover:bg-indigo-700',
        primaryActiveClass: 'active:bg-indigo-800'
      },
      purple: {
        primary: '#9333ea', // purple-600
        primaryHover: '#7c3aed', // purple-700
        primaryActive: '#6b21a8', // purple-800
        primaryClass: 'bg-purple-600',
        primaryHoverClass: 'hover:bg-purple-700',
        primaryActiveClass: 'active:bg-purple-800'
      },
      green: {
        primary: '#16a34a', // green-600
        primaryHover: '#15803d', // green-700
        primaryActive: '#166534', // green-800
        primaryClass: 'bg-green-600',
        primaryHoverClass: 'hover:bg-green-700',
        primaryActiveClass: 'active:bg-green-800'
      },
      red: {
        primary: '#dc2626', // red-600
        primaryHover: '#b91c1c', // red-700
        primaryActive: '#991b1b', // red-800
        primaryClass: 'bg-red-600',
        primaryHoverClass: 'hover:bg-red-700',
        primaryActiveClass: 'active:bg-red-800'
      },
      orange: {
        primary: '#ea580c', // orange-600
        primaryHover: '#c2410c', // orange-700
        primaryActive: '#9a3412', // orange-800
        primaryClass: 'bg-orange-600',
        primaryHoverClass: 'hover:bg-orange-700',
        primaryActiveClass: 'active:bg-orange-800'
      },
      teal: {
        primary: '#0d9488', // teal-600
        primaryHover: '#0f766e', // teal-700
        primaryActive: '#115e59', // teal-800
        primaryClass: 'bg-teal-600',
        primaryHoverClass: 'hover:bg-teal-700',
        primaryActiveClass: 'active:bg-teal-800'
      },
      gray: {
        primary: '#4b5563', // gray-600
        primaryHover: '#374151', // gray-700
        primaryActive: '#1f2937', // gray-800
        primaryClass: 'bg-gray-600',
        primaryHoverClass: 'hover:bg-gray-700',
        primaryActiveClass: 'active:bg-gray-800'
      }
    };

    return colorMap[color] || colorMap.blue;
  };

  const buttonColors = useMemo(() => getButtonColors(menuColor, customMenuColor), [menuColor, customMenuColor]);
  const greyMenuColors = useMemo(() => getButtonColors(greyMenuColor, customGreyMenuColor), [greyMenuColor, customGreyMenuColor]);

  return {
    buttonColors,
    greyMenuColors,
    menuColor,
    customMenuColor,
    greyMenuColor,
    customGreyMenuColor
  };
};
